import AddEmailToPersonModal from '@/components/AddEmailToPersonModal';
import CategoriesMenu from '@/components/CategoriesMenu';
import CreateLeverageRequestModal, { LeverageRequestPromise } from '@/components/CreateLeverageRequestModal';
import DeleteActionModal from '@/components/DeleteActionModal';
import IconProgressState from '@/components/IconProgressState';
import IconStarred from '@/components/IconStarred';
import Input from '@/components/Input';
import InputDuration from '@/components/InputDuration';
import LeverageRequestModal from '@/components/LeverageRequestModal';
import ManageActionRelations from '@/components/ManageActionRelations';
import ProjectsMenu from '@/components/ProjectsMenu';
import RPMDateTimePickerWithPlaceholder from '@/components/RPMDateTimePicker/RPMDateTimePickerWithPlaceholder';
import RecurrenceMenu from '@/components/RecurrenceMenu/RecurrenceMenu';
import RecurringConfirmationModal from '@/components/RecurringEventModal/ConfirmationModal';
import RecurringEventModal, { EventOptions } from '@/components/RecurringEventModal/RecurringEventModal';
import DeleteRecurringEventModal from '@/components/RecurringEventModal/RecurringEventModal';
import StyledModal from '@/components/StyledModal';
import { ActionRelation, INCOMPLETE } from '@/constants/action';
import { DAY, WEEK, WEEKDAYS, WEEKDAYS_MONDAY_FIRST, WEEKDAY_ABBREV_ES, weekdayOnly } from '@/constants/calendar';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useSoundEffects } from '@/contexts/SoundEffects';
import { UPDATE_ACTION_ONE } from '@/gql/actions';
import { CustomRecurrenceData, RecurrenceRule } from '@/gql/events/types';
import { keys } from '@/gql/global/keys';
import useUpdateBlockAction from '@/hooks/useBlockActions';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import useIsDarkMode from '@/hooks/useIsDarkMode';
import { RoutesList } from '@/routes/routesList';
import { useCreateAction, useCreatePromises, useDeleteAction, useDeletePromisesByAction, useReplacePromises, useUpdateActionStatus, useUpdatePromise, useUpdatePromiseRemoveLeverageRequest } from '@/services/action/hooks';
import { useCategories } from '@/services/categories/hooks';
import { useCronofySyncEvent } from '@/services/cronofy/hooks';
import { useCreateEvent, useUpdateEventByPk } from '@/services/events/hooks';
import { fetchData } from '@/services/graphql';
import { queryClient } from '@/services/graphql/queryClient';
import { usePeople } from '@/services/people/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import {
  getOrCreateWeeklyPlanId,
  getWeeklyPlanId,
  useLazyWeeklyPlan,
  useWeeklyPlanId,
} from '@/services/plans/hooks/useWeeklyPlan';
import { useProjects } from '@/services/project/hooks';
import { useUserInfo } from '@/services/user/hooks';
import { useActionModal } from '@/stores/useActionModal';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { IconTrash, IconWarning } from '@/theme/icons';
import { Action, ProgressStatus, UpdateActionResponse } from '@/types/actions';
import { DayWeek } from '@/types/calendar';
import { Category } from '@/types/category';
import { ActionEventType } from '@/types/myPlan';
import { ProjectType } from '@/types/project';
import { UserGroup } from '@/types/user';
import { pairedActionScheduleFields, refetchActions } from '@/utils/action';
import {
  alignLocalDatetimeToFirstWeeklyByDay,
  computeRecurrenceExceptionDates,
  formatDateToString,
  formatStringToDate,
  getGMTOffset,
  localToGivenTimezoneDateToUTC,
  startOfTheWeek,
  utcToLocalDate,
  weeklyByDayAnchorNeedsAlignment,
} from '@/utils/calendar';
import { getCategoryById } from '@/utils/category';
import { dayAbbrev, getFrequency, getValueInRecurrenceRule, isEveryWeekdayLabel, normalizeRecurrenceRuleFrequency, validateRecurrence } from '@/utils/events';
import { capitalizeFirstLetter, checkIfOnlySpaces } from '@/utils/index';
import rem from '@/utils/rem';
import { invalidateQueries as invalidateTanStackQueries } from '@/utils/tanStackQuery';
import { EventLocation, trackActionCreated, trackActionProgress, trackActionStarred, trackCommitmentMade, trackCreateActionStarted } from '@/utils/tracking';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  HStack,
  Icon,
  IconButton,
  ModalBody,
  ModalFooter,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useBoolean,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { format, isSameDay } from 'date-fns';
import { difference, isEmpty, isEqual, isNull, isUndefined } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ITimezone } from 'react-timezone-select';
import { useTimeout } from 'usehooks-ts';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

export type ActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreateAction?: (action: Action) => void;
  onDeleteAction?: (action: Action) => void;
  onCreateEvent?: (event: ActionEventType) => void;
  readonlyCategory?: boolean;
  readonlyTooltipLabel?: string;
  showProjectSelector?: boolean;
  blockId?: string;
  categoryId?: string;
  initialRelation?: ActionRelation;
  personId?: string;
  projectId?: string;
  title?: string;
  readonlyProject?: boolean;
  withPlaceholder?: boolean;
  location?: EventLocation;
  preSelectedDate?: Date;
  defaultActionDuration?: string;
  preSelectedTime?: Date;
  isOpenFromRow?: boolean;
  disallowActionRecurrence?: boolean;
};

// Define the schema outside the component but create a function
// to generate the schema with translations
function createActionFormSchema(t: (key: string) => string) {
  return z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t('validation.actionNameRequiresAtLeast1Character') })
      .max(300, { message: t('validation.actionNameMustBe300CharactersOrLess') }),
    time: z
      .string()
      .trim()
      .refine((value) => !!value, {
        message: t('validation.setMinimumDurationInFormatHhMm'),
      })
      .refine(
        (value) => {
          const [hours, minutes] = value.split(':').map(Number);
          if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            return false;
          }
          return true;
        },
        {
          message: t('validation.invalidTimeFormat'),
        },
      )
      .refine(
        (value) => {
          const [hours, minutes] = value.split(':').map(Number);

          if (hours === 0 && minutes < 5) {
            return false;
          }
          return true;
        },
        {
          message: t('validation.theMinimumDurationIs5Minutes'),
        },
      ),
    notes: z.string().optional(),
    createLeverageRequest: z.boolean().optional(),
  });
}

// Static schema for type inference - used for type extraction
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActionFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'Action name requires at least 1 character' })
    .max(300, { message: 'Action name must be 300 characters or less' }),
  time: z
    .string()
    .trim()
    .refine((value) => !!value, {
      message: 'Set a minimum duration in the format hh:mm',
    })
    .refine(
      (value) => {
        const [hours, minutes] = value.split(':').map(Number);
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
          return false;
        }
        return true;
      },
      {
        message: 'Invalid time format',
      },
    )
    .refine(
      (value) => {
        const [hours, minutes] = value.split(':').map(Number);

        if (hours === 0 && minutes < 5) {
          return false;
        }
        return true;
      },
      {
        message: 'The minimum duration is 5 minutes',
      },
    ),
  notes: z.string().optional(),
  createLeverageRequest: z.boolean().optional(),
});

type ActionFormData = z.infer<typeof ActionFormSchema>;

/** Normalize schedule values so ISO and yyyy-MM-dd compare equal (avoids false "date changed"). */
const normalizeScheduleDateKey = (value: string | null | undefined): string | null => {
  if (value == null || value === '') return null;
  return String(value).trim().split('T')[0].slice(0, 10);
};

const normalizeRecurrenceUntilKey = (value: string | null | undefined): string | null => {
  if (value == null || value === '') return null;
  return String(value).trim().split('T')[0].slice(0, 10);
};

const recurrenceByDaysSortKey = (days: { day: string }[] | undefined): string =>
  (days ?? [])
    .map((d) => (d?.day ?? '').toLowerCase())
    .sort()
    .join(',');

/**
 * Same cases as `handleUpdatePromises`: first assignment, clearing everyone,
 * editing the lone promise, or a different set of people than before.
 */
function havePromiseAssignmentsChanged(
  savedPromises: Action['promises'] | undefined,
  relation: ActionRelation,
  selectedPeopleIds: string[],
): boolean {
  const saved = savedPromises ?? [];
  const hadPromises = saved.length > 0;
  const selected = selectedPeopleIds;

  if (!hadPromises) {
    return relation !== ActionRelation.None && selected.length > 0;
  }

  if (selected.length === 0) {
    return true;
  }

  if (saved.length === 1 && selected.length === 1) {
    const [only] = saved;
    return only.kind !== relation || only.personId !== selected[0];
  }

  const savedPersonIds = saved.map((p) => p.personId);
  const removedSomeone = difference(savedPersonIds, selected).length > 0;
  const addedSomeone = difference(selected, savedPersonIds).length > 0;
  return removedSomeone || addedSomeone;
}

const ActionModal: FC<ActionModalProps> = ({
  isOpen,
  onClose,
  onCreateAction,
  onDeleteAction,
  onCreateEvent,
  readonlyCategory = false,
  readonlyTooltipLabel = undefined,
  showProjectSelector = true,
  readonlyProject,
  withPlaceholder,
  categoryId,
  initialRelation = ActionRelation.None,
  personId,
  projectId,
  blockId,
  location: eventLocation,
  title,
  preSelectedDate,
  defaultActionDuration = '00:05',
  preSelectedTime,
  isOpenFromRow = false,
  disallowActionRecurrence = false,
}) => {
  const { t } = useTranslation('ActionModal');
  const { t: tRecurrence, i18n } = useTranslation('RecurrenceMenu');
  const toast = useToast();
  const location = useLocation();
  const { data: people } = usePeople();
  const isDailyPlanPage = useIsDailyPlanPage();
  const isDarkMode = useIsDarkMode();
  const createEvent = useCreateEvent({
    onSuccess: ({ insertEventOne }) => onCreateEvent && onCreateEvent(insertEventOne),
  });

  const createAction = useCreateAction();
  const deletePromises = useDeletePromisesByAction();
  const createPromises = useCreatePromises();
  const updatePromise = useUpdatePromise();
  const replacePromises = useReplacePromises();
  const updatePromiseRemoveLeverageRequest = useUpdatePromiseRemoveLeverageRequest();

  const currentActionData = useActionModal((state) => state.action);

  const recurringActionIsDisabledInBlock = Boolean(
    blockId || currentActionData?.blockId || disallowActionRecurrence,
  );

  const initialTabIndex = currentActionData?.scheduledDate || preSelectedDate || isDailyPlanPage ? DAY : WEEK;

  const isEditingAction = !!currentActionData?.id;

  const initialized = useRef(false);
  const resolvedWeeklyPlanIdRef = useRef<string | null | undefined>(undefined);
  const pendingRecurrenceRef = useRef<{
    customRecurrence: CustomRecurrenceData | undefined;
    selectedRecurrence: string;
  } | null>(null);
  /** Preserves series `until` when splitting with "this and following" so the new action keeps the original end date. */
  const pendingOriginalUntilForFollowingRef = useRef<string | undefined>(undefined);

  // Feature flag checks
  const { isEnabled: isEnabledForBeta } = useFeatureFlag(FEATURE_FLAGS.SHOW_RECURRENT_EVENTS_FOR_BETA_USERS);
  const { isEnabled: isEnabledForAll } = useFeatureFlag(FEATURE_FLAGS.SHOW_RECURRENT_EVENTS_FOR_ALL_USERS);

  const { data: userInfo } = useUserInfo();
  const isBetaUser = userInfo?.me?.userGroup === UserGroup.BETA || userInfo?.me?.userGroup === UserGroup.ADMIN;

  const shouldShowRecurrenceFeature = isEnabledForAll || (isEnabledForBeta && isBetaUser);

  useEffect(() => {
    if (!isEditingAction && !initialized.current && eventLocation) {
      trackCreateActionStarted(eventLocation);
      initialized.current = true;
    }
  }, [isEditingAction, eventLocation]);

  const [isStarred, setIsStarred] = useState<Date | null>(null);
  const [completionStatus, setCompletionStatus] = useState<ProgressStatus>(
    currentActionData?.progressStatus ?? 'incomplete',
  );
  const [isDeleteModal, setIsDeleteModal] = useBoolean();
  const [isDeleteRecurringEventModal, setIsDeleteRecurringEventModal] = useBoolean();
  const [isEditRecurringEvent, setIsEditRecurringEvent] = useBoolean();
  const [isAllowThisRecurringEvent, setIsAllowThisRecurringEvent] = useBoolean();
  const [isAllowAllRecurringEvent, setIsAllowAllRecurringEvent] = useBoolean();

  const { isOpen: isDatePickerOpen, onOpen: onDatePickerOpen, onClose: onDatePickerClose } = useDisclosure();

  const {
    isOpen: isOpenLeverageRequestModal,
    onOpen: openLeverageRequestModal,
    onClose: onCloseLeverageRequestModal,
  } = useDisclosure();

  const { isOpen: isAddEmailModalOpen, onOpen: onAddEmailModalOpen, onClose: onAddEmailModalClose } = useDisclosure();

  const {
    isOpen: isOpenRecurringConfirmationModal,
    onOpen: openRecurringConfirmationModal,
    onClose: onCloseRecurringConfirmationModal,
  } = useDisclosure();

  const {
    onGetCurrentActionData: getCurrentActionData,
    setCreateLeverageModalOpen,
    createLeverageModalOpen,
  } = useActionModal();

  const getFormattedRecurrenceLabel = (
    recurrence: string | null,
    interval: number = 1,
    byDays: { day: string }[] = [],
    scheduledDate?: string | Date,
  ): string | null => {
    if (!recurrence) return null;

    const formattedDays = byDays?.map((d) => d.day) || [];
    const sortedDays = [...formattedDays].sort(
      (a, b) => WEEKDAYS_MONDAY_FIRST.indexOf(a.toLowerCase()) - WEEKDAYS_MONDAY_FIRST.indexOf(b.toLowerCase()),
    );

    const isWeekdayOnly =
      formattedDays.length === weekdayOnly.length &&
      formattedDays.every((day) => weekdayOnly.some((wd) => wd.day === day));

    const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
    const dayAbbrevMap = lang === 'es' ? WEEKDAY_ABBREV_ES : dayAbbrev;
    const abbrevDaysLocalized = sortedDays.map(
      (d) => dayAbbrevMap[d.toLowerCase()] ?? capitalizeFirstLetter(d).slice(0, 3),
    );
    const formatDateLocale = (d: Date) =>
      d.toLocaleDateString(lang === 'es' ? 'es' : 'en-US', { month: 'short', day: 'numeric' });
    const formatMonthDayOrdinal = (d: Date) => {
      const day = d.getDate();
      const suffix =
        lang === 'en'
          ? day === 1 || day === 21 || day === 31
            ? 'st'
            : day === 2 || day === 22
              ? 'nd'
              : day === 3 || day === 23
                ? 'rd'
                : 'th'
          : '';
      return lang === 'es' ? String(day) : `the ${day}${suffix}`;
    };

    if (recurrence === 'daily' && interval > 1) {
      return tRecurrence('chip.everyNDays', { n: interval });
    }
    if (recurrence === 'daily') {
      return tRecurrence('chip.daily');
    }

    if (recurrence === 'weekly' && Array.isArray(byDays) && byDays.length > 0) {
      return isWeekdayOnly
        ? tRecurrence('chip.everyWeekday')
        : interval > 1
          ? tRecurrence('chip.everyNWeeksOn', { n: interval, days: abbrevDaysLocalized.join(', ') })
          : tRecurrence('chip.everyWeekOn', { days: abbrevDaysLocalized.join(', ') });
    }

    if (recurrence === 'monthly' && interval > 1 && scheduledDate) {
      const date = new Date(scheduledDate);
      return tRecurrence('chip.everyNMonthsOn', { n: interval, date: formatMonthDayOrdinal(date) });
    }

    if (recurrence === 'monthly' && interval > 1) {
      return tRecurrence('chip.everyNMonths', { n: interval });
    }

    if (recurrence === 'yearly' && interval > 1) {
      return tRecurrence('chip.everyNYears', { n: interval });
    }

    if (scheduledDate) {
      const date = new Date(scheduledDate);

      if (recurrence === 'weekly') {
        const dayKey = WEEKDAYS[date.getDay()];
        const abbrev = dayAbbrevMap[dayKey] ?? dayKey?.slice(0, 3);
        return tRecurrence('chip.weeklyOn', { day: abbrev });
      }

      if (recurrence === 'monthly') {
        return tRecurrence('chip.monthlyOn', { date: formatMonthDayOrdinal(date) });
      }

      if (recurrence === 'yearly') {
        return tRecurrence('chip.yearlyOn', { date: formatDateLocale(date) });
      }
    }

    return recurrence;
  };

  const [selectedCategory, setSelectedCategory] = useState<Category>();

  const { data: projects } = useProjects();

  const [selectedProject, setSelectedProject] = useState<ProjectType | null>(null);

  const frequency = getValueInRecurrenceRule(currentActionData?.event?.recurrence, 'frequency');

  const recurrenceRule = currentActionData?.event?.recurrence?.rules?.[0];
  const byDays = recurrenceRule?.by_day;

  const interval = getValueInRecurrenceRule(currentActionData?.event?.recurrence, 'interval') || 1;

  /** Count from saved rule; undefined when Ends = Never (do not default to 1 so modal shows "Never" on reopen) */
  const recurrenceCountFromRule = getValueInRecurrenceRule(currentActionData?.event?.recurrence, 'count');

  // until is stored as yyyy-MM-dd; new Date("yyyy-MM-dd") is UTC midnight and shows as previous calendar day in many timezones.
  const savedEndDateFromAction = recurrenceRule?.until
    ? formatStringToDate(String(recurrenceRule.until).split('T')[0])
    : undefined;

  const formattedFrequency = getFormattedRecurrenceLabel(
    frequency,
    +interval,
    byDays,
    currentActionData?.scheduledDate
      ? utcToLocalDate(
          currentActionData?.scheduledDate,
          currentActionData?.scheduledTime,
          currentActionData?.timezone,
          currentActionData?.gmtOffset,
        )
      : undefined,
  );

  const [selectedRecurrence, setSelectedRecurrence] = useState<string>(capitalizeFirstLetter(formattedFrequency || ''));

  const isSeriesLevelRecurringStatusReadOnly = useMemo(
    () =>
      isOpenFromRow &&
      isEditingAction &&
      Boolean(selectedRecurrence) &&
      validateRecurrence(currentActionData?.event?.recurrence),
    [isOpenFromRow, isEditingAction, selectedRecurrence, currentActionData?.event?.recurrence],
  );

  const [customRecurrence, setCustomRecurrence] = useState<CustomRecurrenceData>();

  const [visibleProjectSection, setVisibleProjectSection] = useState(false);

  const showProjectSection = () => setVisibleProjectSection(true);

  useTimeout(showProjectSection, 1500);

  const [tabIndex, setTabIndex] = useState<typeof DAY | typeof WEEK>(initialTabIndex);

  const [dateRange, setDateRange] = useState(tabIndex === WEEK);

  const [relation, setRelation] = useState<ActionRelation>(
    isEditingAction ? (currentActionData?.promises?.[0]?.kind ?? ActionRelation.None) : initialRelation,
  );

  const [selectedPeople, setSelectedPeople] = useState<string[]>(() =>
    isEditingAction
      ? currentActionData?.promises?.map((promise) => promise.personId) || []
      : personId
        ? [personId]
        : [],
  );

  const [leverageRequestPromise, setLeverageRequestPromise] = useState<LeverageRequestPromise | null>(null);

  const isUnplanned = !currentActionData?.weeklyPlanId;

  // Use local date in action's timezone when we have scheduledTime, so the week and date picker
  // show the correct calendar day (e.g. Sunday 6 PM MST stays on Sunday, not Monday UTC).
  const actionDate = !isUnplanned
    ? currentActionData?.scheduledTime && currentActionData?.timezone
      ? utcToLocalDate(
          currentActionData.scheduledDate as string,
          currentActionData.scheduledTime,
          currentActionData.timezone,
          currentActionData.gmtOffset,
        )
      : formatStringToDate(
          (currentActionData?.scheduledDate
            ? currentActionData?.scheduledDate
            : currentActionData?.weekly_plan?.dateFrom) as string,
        )
    : null;

  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);

  const [scheduledDate, setScheduledDate] = useState<Date | null>(
    isEditingAction ? actionDate : withPlaceholder ? null : (preSelectedDate ?? selectedDate),
  );

  const scheduleDateForRecurrence =
    scheduledDate ??
    (currentActionData?.scheduledDate && currentActionData?.scheduledTime && currentActionData?.timezone
      ? utcToLocalDate(
          currentActionData.scheduledDate as string,
          currentActionData.scheduledTime,
          currentActionData.timezone,
          currentActionData.gmtOffset,
        )
      : currentActionData?.scheduledDate
        ? formatStringToDate(currentActionData.scheduledDate as string)
        : undefined);

  const displayRecurrenceLabel = useMemo(() => {
    const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
    const dayAbbrevMap = lang === 'es' ? WEEKDAY_ABBREV_ES : dayAbbrev;
    const formatDays = (days: { day: string }[]) => {
      if (!days?.length) return '';
      const sorted = [...days].sort(
        (a, b) =>
          WEEKDAYS_MONDAY_FIRST.indexOf((a.day ?? '').toLowerCase()) -
          WEEKDAYS_MONDAY_FIRST.indexOf((b.day ?? '').toLowerCase()),
      );
      return sorted.map((d) => dayAbbrevMap[d.day?.toLowerCase()] ?? d.day?.slice(0, 3)).join(', ');
    };
    const formatDate = (d: Date) =>
      d.toLocaleDateString(lang === 'es' ? 'es' : 'en-US', { month: 'short', day: 'numeric' });

    const rawFreq = customRecurrence
      ? (customRecurrence.selectedRecurrence || '').toLowerCase()
      : selectedRecurrence
        ? (frequency || '').toLowerCase()
        : '';
    const freqMap: Record<string, string> = {
      daily: 'day',
      weekly: 'week',
      monthly: 'month',
      yearly: 'year',
    };
    const freq = freqMap[rawFreq] ?? rawFreq;
    const int = customRecurrence?.interval ?? (interval ? +interval : 1);
    const days = customRecurrence?.selectedWeekDays ?? byDays ?? [];
    const refDate = customRecurrence?.scheduledDate ?? scheduleDateForRecurrence ?? undefined;
    const recurrenceStartDate = scheduleDateForRecurrence ?? undefined;
    const isWeekdayOnly =
      days.length === weekdayOnly.length &&
      days.every((d) => weekdayOnly.some((wd) => wd.day === (d.day ?? '').toLowerCase()));

    if (!freq && !selectedRecurrence) return null;
    if (freq === 'day') return int > 1 ? tRecurrence('chip.everyNDays', { n: int }) : tRecurrence('chip.daily');
    if (freq === 'week') {
      if (isWeekdayOnly) return tRecurrence('chip.everyWeekday');
      const daysStr = formatDays(days);
      if (daysStr)
        return int > 1
          ? tRecurrence('chip.everyNWeeksOn', { n: int, days: daysStr })
          : tRecurrence('chip.everyWeekOn', { days: daysStr });
      if (refDate) {
        const dayKey = WEEKDAYS[refDate.getDay()];
        const abbrev = dayAbbrevMap[dayKey] ?? dayKey?.slice(0, 3);
        return tRecurrence('chip.weeklyOn', { day: abbrev });
      }
    }
    if (freq === 'month') {
      if (recurrenceStartDate) {
        const day = recurrenceStartDate.getDate();
        const suffix =
          lang === 'en'
            ? day === 1 || day === 21 || day === 31
              ? 'st'
              : day === 2 || day === 22
                ? 'nd'
                : day === 3 || day === 23
                  ? 'rd'
                  : 'th'
            : '';
        const dateStr = lang === 'es' ? String(day) : `the ${day}${suffix}`;
        return int > 1
          ? tRecurrence('chip.everyNMonthsOn', { n: int, date: dateStr })
          : tRecurrence('chip.monthlyOn', { date: dateStr });
      }
      return int > 1 ? tRecurrence('chip.everyNMonths', { n: int }) : null;
    }
    if (freq === 'year') {
      if (int > 1) return tRecurrence('chip.everyNYears', { n: int });
      if (recurrenceStartDate) return tRecurrence('chip.yearlyOn', { date: formatDate(recurrenceStartDate) });
      return null;
    }
    return selectedRecurrence || null;
  }, [
    i18n.language,
    customRecurrence,
    frequency,
    interval,
    byDays,
    scheduleDateForRecurrence,
    selectedRecurrence,
    tRecurrence,
  ]);

  const selectedPerson = useMemo(() => {
    if (people?.person && selectedPeople.length) {
      return people.person.find((person) => person.id === selectedPeople[0]);
    }
  }, [people?.person, selectedPeople]);

  const leverageStatus = currentActionData?.promises?.length
    ? currentActionData?.promises[0].leverageRequestStatus
    : undefined;
  // a leverage request can be created if there is no leverageRequestStatus on the current promise
  // or if the user has changed the person to which the promise was previously assigned
  const currentlyLeveragedPersonId = currentActionData?.promises?.[0]?.personId;
  const willBeNewPersonLeveraged =
    currentlyLeveragedPersonId !== selectedPerson?.id && relation === ActionRelation.Leverage;
  const allowCreateLeverageRequest = !!leverageStatus === false || willBeNewPersonLeveraged;

  const hasDateChanged = useMemo(() => {
    if (!scheduledDate) {
      return false;
    }

    return !isSameDay(
      utcToLocalDate(format(scheduledDate, 'yyyy-MM-dd'), null),
      tabIndex === DAY ? scheduledDate : startOfTheWeek(scheduledDate),
    );
  }, [scheduledDate, tabIndex]);

  const currentView = isDailyPlanPage ? DAY : WEEK;
  const hasChangedTabs = tabIndex !== currentView;

  const [scheduledTime, setScheduledTime] = useState<Date | null>(preSelectedTime ?? null);
  const [, setResolveConfirmPromise] = useState<(() => (value: boolean) => void) | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [formData, setFormData] = useState<ActionFormData>();

  const updateActionStatus = useUpdateActionStatus();

  const { playCompleteAudio } = useSoundEffects();

  const { data: categoriesList } = useCategories();

  const timeZone = typeof selectedTimezone === 'string' ? selectedTimezone : selectedTimezone.value;

  // Combine date picker + time picker in the action's timezone, then convert to UTC.
  // Using only scheduledTime would use "today" from the time picker and assign the wrong week (e.g. Sunday 6 PM MST showing on week starting Monday).
  const combinedDateAndTime =
    scheduledDate && scheduledTime
      ? (() => {
          const combined = new Date(scheduledDate);
          combined.setHours(scheduledTime.getHours(), scheduledTime.getMinutes(), scheduledTime.getSeconds(), 0);
          return localToGivenTimezoneDateToUTC(combined, timeZone);
        })()
      : null;
  const scheduledDateWithTimeFormatted = combinedDateAndTime?.scheduledDate ?? null;
  const scheduledDateFormatted = scheduledDate && format(scheduledDate, 'yyyy-MM-dd');

  const scheduledDateUpdated =
    tabIndex === DAY ? (scheduledDateWithTimeFormatted ?? scheduledDateFormatted) : currentActionData?.scheduledDate;

  /** Same pipeline as scheduledDateUpdated but from loaded action (occurrence date), not event.series start */
  const scheduledDateUpdatedFromStoredAction = useMemo(() => {
    if (!currentActionData) return null;
    if (tabIndex !== DAY) {
      return currentActionData.scheduledDate ?? null;
    }
    if (!currentActionData.scheduledDate) return null;
    const localBase =
      currentActionData.scheduledTime && currentActionData.timezone
        ? utcToLocalDate(
            currentActionData.scheduledDate as string,
            currentActionData.scheduledTime,
            currentActionData.timezone,
            currentActionData.gmtOffset,
          )
        : formatStringToDate(currentActionData.scheduledDate as string);
    if (!localBase) return currentActionData.scheduledDate;
    if (!currentActionData.scheduledTime) {
      return format(localBase, 'yyyy-MM-dd');
    }
    const combined = new Date(localBase);
    combined.setHours(localBase.getHours(), localBase.getMinutes(), localBase.getSeconds(), 0);
    const utcPack = localToGivenTimezoneDateToUTC(combined, timeZone);
    return utcPack?.scheduledDate ?? format(localBase, 'yyyy-MM-dd');
  }, [currentActionData, tabIndex, timeZone]);

  const scheduleChangedVsLoaded =
    normalizeScheduleDateKey(scheduledDateUpdated) !== normalizeScheduleDateKey(scheduledDateUpdatedFromStoredAction);

  const persistedScheduledDateForUpdateMutation =
    scheduledDate === null
      ? null
      : tabIndex === WEEK
        ? null
        : currentActionData?.event?.recurrence?.rules?.length &&
            !scheduleChangedVsLoaded &&
            currentActionData?.event?.start
          ? currentActionData.event.start.split('T')[0]
          : scheduledDateUpdated;

  const readOnlyTooltip = currentActionData?.blockId
    ? t('tooltips.categoryCannotBeChangedBecauseItIsIncludedInABlock')
    : readonlyTooltipLabel;

  // always get the weeklyplan for the scheduledDateUpdated if defined, this also means we account
  // for the selected timezone (which could take the date into a different week)
  const weeklyPlanDate =
    scheduledDateFormatted && tabIndex === DAY
      ? formatStringToDate(scheduledDateFormatted)
      : (scheduledDate ?? new Date());

  const { data: weeklyPlanId } = useWeeklyPlanId(weeklyPlanDate);
  const { getWeeklyPlan, weeklyPlanQueryResult } = useLazyWeeklyPlan({
    selectedDate: weeklyPlanDate,
  });

  const hasNewWeeklyPlan = !isUndefined(weeklyPlanId) && currentActionData?.weeklyPlanId !== weeklyPlanId;

  // Create the localized schema using our function
  const localizedSchema = useMemo(() => createActionFormSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setFocus,
    setValue,
    formState: { errors },
  } = useForm<ActionFormData>({
    defaultValues: {
      name: title ?? currentActionData?.title ?? '',
      time: currentActionData?.duration ?? defaultActionDuration,
      notes: currentActionData?.notes ?? '',
      createLeverageRequest: !!leverageStatus,
    },
    resolver: zodResolver(localizedSchema),
    mode: 'onChange',
  });

  const actionDataWatcher = watch('name');
  const timeDataWatcher = watch('time');
  const notesDataWatcher = watch('notes');
  const createLeverageRequestWatcher = watch('createLeverageRequest');

  const onHandlePlansIds = useCallback(() => {
    onDatePickerClose();
    if (scheduledDate !== currentActionData?.scheduledDate || hasChangedTabs) {
      getWeeklyPlan();
    }
  }, [onDatePickerClose, scheduledDate, currentActionData?.scheduledDate, hasChangedTabs, getWeeklyPlan]);

  const onSetCategory = useCallback(() => {
    let currentCategory;

    if (isEditingAction) {
      currentCategory = categoriesList?.category?.find((el: Category) => el?.id === currentActionData?.categoryId);
    } else {
      if (categoriesList?.category && categoryId) {
        currentCategory = getCategoryById(categoriesList?.category, categoryId);
      } else {
        currentCategory = currentCategory = categoriesList?.category?.find(
          (category) => category.name.toLowerCase() === 'uncategorized',
        );
      }
    }
    if (currentCategory) {
      setSelectedCategory(currentCategory);
    }
  }, [categoriesList?.category, currentActionData?.categoryId, categoryId, isEditingAction]);

  const onSetProject = useCallback(() => {
    const currentProject = projects?.project.find(
      (el: ProjectType) => el?.id === (isEditingAction ? currentActionData?.projectId : projectId),
    );

    setSelectedProject(currentProject || null);
  }, [projects?.project, isEditingAction, projectId, currentActionData?.projectId]);

  const { mutate: cronofySyncEvent } = useCronofySyncEvent();
  const updateBlockAction = useUpdateBlockAction();

  const updateEvent = useUpdateEventByPk();

  const onUpdateActionOne = useMutation({
    mutationFn: () =>
      fetchData<UpdateActionResponse>(
        UPDATE_ACTION_ONE,
        pairedActionScheduleFields({
          duration: timeDataWatcher,
          title: actionDataWatcher,
          categoryId: selectedCategory?.id,
          actionId: currentActionData?.id,
          blockId: currentActionData?.projectId !== (selectedProject?.id ?? null) ? null : currentActionData?.blockId,
          projectId: selectedProject?.id,
          blockOrder:
            currentActionData?.projectId !== (selectedProject?.id ?? null) ? null : (currentActionData?.blockOrder ?? 1),
          dateOfStarring: isStarred,
          isLocked: currentActionData?.isLocked ?? false,
          notes: notesDataWatcher,
          weeklyPlanId:
            scheduledDate === null
              ? null
              : resolvedWeeklyPlanIdRef.current ?? (hasNewWeeklyPlan ? weeklyPlanId : currentActionData?.weeklyPlanId),
          progressStatus: completionStatus,
          categoryOrder: currentActionData?.categoryOrder,
          scheduledDate: persistedScheduledDateForUpdateMutation,
          scheduledTime: combinedDateAndTime?.scheduledTime ?? (scheduledTime ? localToGivenTimezoneDateToUTC(scheduledTime, timeZone).scheduledTime : null),
          timezone: timeZone ?? currentActionData?.timezone,
          globalCommitOrder: currentActionData?.globalCommitOrder,
          globalLeverageOrder: currentActionData?.globalLeverageOrder,
          gmtOffset: getGMTOffset(timeZone) ?? currentActionData?.gmtOffset,
        } as Record<string, unknown>),
      ),

    onSuccess: async (data) => {
      resolvedWeeklyPlanIdRef.current = undefined;
      const snapshot = pendingRecurrenceRef.current;
      pendingRecurrenceRef.current = null;
      const customRecurrenceAtSave = snapshot?.customRecurrence ?? customRecurrence;
      const selectedRecurrenceAtSave = snapshot?.selectedRecurrence ?? selectedRecurrence;

      if (shouldSyncEvent()) {
        cronofySyncEvent({ eventId: data.updateActionByPk.id, eventType: 'Action' });
      }
      const selectedFrequency = selectedRecurrenceAtSave?.toLowerCase();
      const currentExceptions = currentActionData?.event?.recurrence?.exceptions || {};

      const currentUntilRaw = recurrenceRule?.until as string | undefined;
      const currentUntilNorm = normalizeRecurrenceUntilKey(currentUntilRaw);
      const currentCount = recurrenceCountFromRule != null ? Number(recurrenceCountFromRule) : undefined;

      const desiredCount =
        customRecurrenceAtSave == null
          ? currentCount
          : customRecurrenceAtSave.occurrences != null
            ? customRecurrenceAtSave.occurrences
            : undefined;

      let desiredUntilNorm: string | null;
      if (customRecurrenceAtSave == null) {
        desiredUntilNorm = currentUntilNorm;
      } else if (customRecurrenceAtSave.scheduledDate != null) {
        desiredUntilNorm = normalizeRecurrenceUntilKey(formatDateToString(customRecurrenceAtSave.scheduledDate));
      } else {
        desiredUntilNorm = null;
      }

      const endsUntilChanged = customRecurrenceAtSave != null && currentUntilNorm !== desiredUntilNorm;
      const endsCountChanged =
        customRecurrenceAtSave != null && (currentCount ?? null) !== (desiredCount ?? null);
      const endsChanged = endsUntilChanged || endsCountChanged;

      const currentInterval = Number(interval ?? 1) || 1;
      const desiredInterval =
        customRecurrenceAtSave == null
          ? currentInterval
          : customRecurrenceAtSave.interval != null
            ? customRecurrenceAtSave.interval
            : currentInterval;
      const recurrenceIntervalChanged =
        customRecurrenceAtSave != null && currentInterval !== desiredInterval;

      const recurrenceByDaysChanged =
        customRecurrenceAtSave != null &&
        Array.isArray(customRecurrenceAtSave.selectedWeekDays) &&
        recurrenceByDaysSortKey(byDays) !== recurrenceByDaysSortKey(customRecurrenceAtSave.selectedWeekDays);

      const frequencyFromForm = getFrequency(
        selectedRecurrenceAtSave || '',
        customRecurrenceAtSave?.selectedRecurrence,
      );
      const canonicalFreqs = ['daily', 'weekly', 'monthly', 'yearly'] as const;
      const frequencyFromFormCanonical =
        frequencyFromForm &&
        canonicalFreqs.includes(frequencyFromForm.toLowerCase() as (typeof canonicalFreqs)[number]);
      const recurrenceFrequencyChanged =
        !!frequency &&
        frequencyFromFormCanonical &&
        frequency.toLowerCase() !== frequencyFromForm.toLowerCase();

      const recurrenceCleared = !!frequency && !selectedFrequency;

      const recurrenceAdded =
        !frequency &&
        frequencyFromFormCanonical &&
        Boolean(selectedFrequency?.trim());

      if (
        !currentActionData?.blockId &&
        (recurrenceCleared ||
          recurrenceFrequencyChanged ||
          recurrenceAdded ||
          scheduleChangedVsLoaded ||
          endsChanged ||
          recurrenceIntervalChanged ||
          recurrenceByDaysChanged)
      ) {
        const recurrenceType = selectedFrequency;

        const isWeekdayOnly = isEveryWeekdayLabel(recurrenceType);

        const weeklyByDay =
          isWeekdayOnly
            ? weekdayOnly
            : customRecurrenceAtSave?.selectedWeekDays?.length
              ? customRecurrenceAtSave.selectedWeekDays
              : (frequencyFromForm || frequency) === 'weekly' && byDays?.length
                ? byDays
                : undefined;

        const intervalResolved =
          customRecurrenceAtSave?.interval != null ? customRecurrenceAtSave.interval : currentInterval;

        let countForRule: number | undefined;
        if (customRecurrenceAtSave?.occurrences != null) {
          countForRule = customRecurrenceAtSave.occurrences;
        } else if (customRecurrenceAtSave != null) {
          // "Never" or "On Date" explicitly selected — clear the existing count
          countForRule = undefined;
        } else if (recurrenceCountFromRule != null) {
          countForRule = Number(recurrenceCountFromRule);
        } else {
          countForRule = undefined;
        }

        let untilForRule: string | undefined;
        if (customRecurrenceAtSave == null) {
          untilForRule = currentUntilRaw ? String(currentUntilRaw).split('T')[0] : undefined;
        } else if (customRecurrenceAtSave.scheduledDate != null) {
          untilForRule = formatDateToString(customRecurrenceAtSave.scheduledDate);
        } else {
          untilForRule = undefined;
        }

        const newRecurrenceRule = {
          frequency: normalizeRecurrenceRuleFrequency(frequencyFromForm || frequency || ''),
          interval: intervalResolved,
          ...(countForRule != null ? { count: countForRule } : {}),
          ...(weeklyByDay && weeklyByDay.length > 0 && { by_day: weeklyByDay }),
          ...(untilForRule ? { until: untilForRule } : {}),
        };

        const weeklyByDayResolved =
          weeklyByDay && weeklyByDay.length > 0 ? weeklyByDay : [];

        let localCombinedForAlign: Date | null = null;
        if (scheduledDate) {
          localCombinedForAlign = new Date(scheduledDate);
          if (scheduledTime) {
            localCombinedForAlign.setHours(
              scheduledTime.getHours(),
              scheduledTime.getMinutes(),
              scheduledTime.getSeconds(),
              0,
            );
          } else {
            localCombinedForAlign.setHours(0, 0, 0, 0);
          }
        }

        const seriesAnchorMisaligned =
          normalizeRecurrenceRuleFrequency(String(newRecurrenceRule.frequency)) === 'weekly' &&
          weeklyByDayResolved.length > 0 &&
          localCombinedForAlign != null &&
          weeklyByDayAnchorNeedsAlignment(localCombinedForAlign, weeklyByDayResolved);

        let recurrenceData: Record<string, unknown> = {};
        let newScheduledTZDate: string | undefined;
        if (!seriesAnchorMisaligned && !scheduleChangedVsLoaded && currentActionData?.event?.start) {
          newScheduledTZDate = currentActionData.event.start.split('T')[0];
        }
        if (!newScheduledTZDate && localCombinedForAlign) {
          const wall = seriesAnchorMisaligned
            ? alignLocalDatetimeToFirstWeeklyByDay(localCombinedForAlign, weeklyByDayResolved)
            : localCombinedForAlign;
          if (scheduledTime) {
            newScheduledTZDate = localToGivenTimezoneDateToUTC(wall, timeZone).scheduledDate;
          } else {
            newScheduledTZDate = formatDateToString(wall);
          }
        }

        const persistedEventEnd = data.updateActionByPk?.event?.end;
        const eventEnd =
          customRecurrenceAtSave?.scheduledDate != null
            ? formatDateToString(customRecurrenceAtSave.scheduledDate)
            : persistedEventEnd ?? null;

        if ((formattedFrequency?.toLocaleLowerCase() || frequency) && !selectedFrequency) {
          recurrenceData = {
            recurrence: {},
            start: null,
            end: null,
          };
        } else if (
          validateRecurrence({
            rules: [newRecurrenceRule],
            exceptions: currentExceptions ?? {},
          })
        ) {
          recurrenceData = {
            recurrence: {
              rules: [newRecurrenceRule],
              exceptions: currentExceptions,
            },
            start: newScheduledTZDate,
            end: eventEnd,
          };
        } else {
          recurrenceData = {};
        }

        if (data.updateActionByPk?.event) {
          try {
            await updateEvent.mutateAsync({
              ...data.updateActionByPk?.event,
              ...(recurrenceData && { ...recurrenceData }),
              id: data.updateActionByPk?.event?.id || '',
            });
          } catch {
            // refetch and close still run
          }
        }
      }

      if (data.updateActionByPk.blockId) {
        updateBlockAction(data.updateActionByPk);
      } else {
        refetchActions(data.updateActionByPk, currentActionData?.weeklyPlanId);
      }

      toast({
        title: t('toasts.actionUpdated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      isAddEmailModalOpen && onAddEmailModalClose();

      if (createLeverageRequestWatcher && allowCreateLeverageRequest) {
        setCreateLeverageModalOpen(true);
        onClose();
      } else {
        onCloseModal();
      }
    },
  });

  const deleteAction = useDeleteAction({
    onSuccess: () => {
      if (isNull(currentActionData)) {
        return;
      }

      onDeleteAction?.(currentActionData);

      refetchActions(currentActionData);

      getCurrentActionData(null);

      if (location.pathname.includes(RoutesList.PeoplePage)) {
        queryClient.refetchQueries(keys.people.all.queryKey);
        queryClient.refetchQueries(keys.actions.leveragedAndCommitted);
      }

      if (currentActionData?.blockId) {
        updateActionStatus.mutate({
          new: null,
          old: {
            id: currentActionData?.id,
            block_id: currentActionData?.blockId,
          },
        });
      }

      toast({
        title: t('toasts.actionDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onCloseModal();
      handleCloseDelete();
    },
  });

  const handleCloseDelete = useCallback(() => {
    setIsDeleteModal.off();
  }, [setIsDeleteModal]);

  const handleDelete = useCallback(() => {
    if (!currentActionData?.id) return;
    deleteAction.mutate({ actionId: currentActionData.id });
  }, [deleteAction, currentActionData?.id]);

  const editRecurringEvents = async (option: EventOptions | null) => {
    setIsEditRecurringEvent.off();
    setIsAllowThisRecurringEvent.off();
    setIsAllowAllRecurringEvent.off();
    const currentExceptions = currentActionData?.event?.recurrence?.exceptions?.add || [];

    const recurrenceRules = currentActionData?.event?.recurrence?.rules?.[0] as RecurrenceRule;

    switch (option) {
      case EventOptions.THIS_EVENT: {
        const newDate = currentActionData?.scheduledDate;
        const dateExists = currentExceptions?.some((e) => e.date === newDate);

        const exceptions: any = dateExists ? currentExceptions : [...currentExceptions, { date: newDate }];

        updateEvent.mutate({
          id: currentActionData?.event?.id as string,
          recurrence: {
            rules: [recurrenceRules],
            exceptions: {
              add: exceptions,
            },
          },
          start: currentActionData?.event?.start,
        });
        await onSubmitCreateAction(formData!, false);
        setFormData(undefined);
        break;
      }
      case EventOptions.THIS_AND_FOLLOWING_EVENTS: {
        if (
          normalizeScheduleDateKey(scheduledDateUpdated) ===
          normalizeScheduleDateKey(currentActionData?.event?.start?.split('T')[0])
        ) {
          // If editing from the first instance forward, it's the same as editing all events
          await handleUpdatePromises();
          pendingRecurrenceRef.current = { customRecurrence, selectedRecurrence };
          onUpdateActionOne.mutate();
          setFormData(undefined);
          break;
        } else {
          const targetDateKey =
            normalizeScheduleDateKey(scheduledDateUpdated) ?? String(scheduledDateUpdated).split('T')[0];
          const { untilUtc, exceptionUtc, exceptions: splitExceptions } = computeRecurrenceExceptionDates({
            targetDate: targetDateKey,
            scheduledTime: currentActionData?.scheduledTime,
            timeZone: currentActionData?.timezone,
            gmtOffset: currentActionData?.gmtOffset,
            existingExceptions: currentExceptions,
            fallbackTimeZone: timeZone,
          });

          pendingOriginalUntilForFollowingRef.current = recurrenceRules?.until
            ? String(recurrenceRules.until).split('T')[0]
            : undefined;

          updateEvent.mutate({
            id: currentActionData?.event?.id as string,
            recurrence: {
              rules: [
                {
                  ...recurrenceRules,
                  until: untilUtc,
                },
              ],
              exceptions: {
                add: splitExceptions,
              },
            },
            start: currentActionData?.event?.start,
            end: exceptionUtc,
          });
        }

        await onSubmitCreateAction(formData!);
        setFormData(undefined);
        break;
      }
      case EventOptions.ALL_EVENTS: {
        if (currentActionData && !currentActionData?.event?.id && scheduledDate) {
          await createEvent.mutateAsync({
            id: uuidv4(),
            actionId: currentActionData?.id,
          });
        }
        await handleUpdatePromises();
        pendingRecurrenceRef.current = { customRecurrence, selectedRecurrence };
        onUpdateActionOne.mutate();
        break;
      }

      default:
        break;
    }
  };

  const checkFormChanges = (
    options: { includeProjectCategory?: boolean; strictFrequencyComparison?: boolean } = {},
  ) => {
    const { includeProjectCategory = true, strictFrequencyComparison = true } = options;

    const currentFrequency = formattedFrequency?.toLocaleLowerCase();
    const currentDuration = currentActionData?.duration?.split(':').slice(0, 2).join(':');
    const newScheduledTime = scheduledTime && localToGivenTimezoneDateToUTC(scheduledTime, timeZone).scheduledTime;
    const formattedScheduledDate = format(scheduledDate || new Date(), 'yyyy-MM-dd');
    const selectedFrequency = selectedRecurrence?.toLowerCase() || null;

    const currentUntilRaw = recurrenceRule?.until as string | undefined;
    const currentUntilNorm = normalizeRecurrenceUntilKey(currentUntilRaw);
    const currentCount = recurrenceCountFromRule != null ? Number(recurrenceCountFromRule) : undefined;
    const desiredCount =
      customRecurrence == null
        ? currentCount
        : customRecurrence.occurrences != null
          ? customRecurrence.occurrences
          : undefined;

    let desiredUntilNorm: string | null;
    if (customRecurrence == null) {
      desiredUntilNorm = currentUntilNorm;
    } else if (customRecurrence.scheduledDate != null) {
      desiredUntilNorm = normalizeRecurrenceUntilKey(formatDateToString(customRecurrence.scheduledDate));
    } else {
      desiredUntilNorm = null;
    }

    const recurrenceEndsChanged =
      (customRecurrence != null && currentUntilNorm !== desiredUntilNorm) ||
      (customRecurrence != null && (currentCount ?? null) !== (desiredCount ?? null));

    const currentInterval = interval ?? 1;
    const desiredInterval = customRecurrence?.interval ?? interval ?? 1;
    const recurrenceIntervalChanged =
      customRecurrence != null && currentInterval !== desiredInterval;

    const recurrenceByDaysChanged =
      customRecurrence != null &&
      Array.isArray(customRecurrence.selectedWeekDays) &&
      recurrenceByDaysSortKey(byDays) !== recurrenceByDaysSortKey(customRecurrence.selectedWeekDays);

    const recurrenceDetailsChanged = recurrenceEndsChanged || recurrenceIntervalChanged || recurrenceByDaysChanged;

    const conditions = [
      strictFrequencyComparison ? currentFrequency !== selectedFrequency : currentFrequency != selectedFrequency,
      formattedScheduledDate !== scheduledDateUpdated,
      currentDuration !== timeDataWatcher,
      currentActionData?.title !== actionDataWatcher,
      currentActionData?.notes !== notesDataWatcher,
      currentActionData?.scheduledTime !== newScheduledTime,
      currentActionData?.scheduledDate !== scheduledDateUpdated,
      recurrenceDetailsChanged,
    ];

    if (includeProjectCategory) {
      conditions.push(
        currentActionData?.categoryId !== selectedCategory?.id,
        currentActionData?.projectId !== (selectedProject?.id ?? null),
      );
    }

    return conditions.some((condition) => condition);
  };

  const shouldSyncEvent = () =>
    checkFormChanges({
      includeProjectCategory: false,
      strictFrequencyComparison: false,
    });

  const isFormValuesChanged = () => checkFormChanges();

  const isStarOrStatusValuesChanged = () => {
    const conditions = [
      currentActionData?.dateOfStarring !== isStarred,
      currentActionData?.progressStatus !== completionStatus,
    ];

    return conditions.some((condition) => condition);
  };

  const showDialogModal = (): Promise<boolean> => {
    openRecurringConfirmationModal();
    return new Promise((resolve) => {
      setResolveConfirmPromise(() => () => resolve);
    });
  };

  const handleCloseDeleteRecurringEvent = useCallback(() => {
    setIsDeleteRecurringEventModal.off();
  }, [setIsDeleteRecurringEventModal]);

  const invalidateProjectPlannerIfNeeded = useCallback(() => {
    if (currentActionData?.projectId) {
      invalidateTanStackQueries([keys.project.detail(currentActionData.projectId).queryKey]);
    }
  }, [currentActionData?.projectId]);

  const deleteRecurringEvents = useCallback(
    async (selectedOption: EventOptions | null) => {
      if (!currentActionData?.id || !currentActionData?.event?.id) return;

      const currentExceptions = currentActionData?.event?.recurrence?.exceptions?.add || [];
      const recurrenceRule = currentActionData?.event?.recurrence?.rules?.[0];
      const untilEvent = getValueInRecurrenceRule(currentActionData?.event?.recurrence, 'until') || '';

      const occurrenceDayKey =
        normalizeScheduleDateKey(scheduledDateUpdated) ??
        normalizeScheduleDateKey(scheduledDateUpdatedFromStoredAction) ??
        normalizeScheduleDateKey(currentActionData?.scheduledDate ?? undefined);

      switch (selectedOption) {
        case EventOptions.THIS_EVENT: {
          if (!occurrenceDayKey) {
            handleCloseDeleteRecurringEvent();
            return;
          }

          const dateExists = currentExceptions?.some(
            (e) => normalizeScheduleDateKey(e.date) === occurrenceDayKey,
          );
          const exceptions = dateExists
            ? currentExceptions
            : [...currentExceptions, { date: occurrenceDayKey }];

          await updateEvent.mutateAsync({
            id: currentActionData.event.id,
            recurrence: {
              rules: [
                {
                  ...(recurrenceRule as any),
                  ...(untilEvent ? { until: untilEvent } : {}),
                },
              ],
              exceptions: {
                add: exceptions,
              },
            },
            start: currentActionData.event.start,
          });

          invalidateProjectPlannerIfNeeded();

          toast({
            title: t('toasts.actionDeleted'),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });

          onClose();
          handleCloseDeleteRecurringEvent();
          break;
        }

        case EventOptions.THIS_AND_FOLLOWING_EVENTS: {
          if (!occurrenceDayKey) {
            handleCloseDeleteRecurringEvent();
            return;
          }

          const seriesStartKey = normalizeScheduleDateKey(currentActionData.event?.start?.split?.('T')?.[0]);

          if (seriesStartKey != null && occurrenceDayKey === seriesStartKey) {
            await deleteAction.mutateAsync({ actionId: currentActionData.id });
            invalidateProjectPlannerIfNeeded();
            handleCloseDeleteRecurringEvent();
            return;
          }

          const { untilUtc, exceptionUtc, exceptions: splitExceptions } = computeRecurrenceExceptionDates({
            targetDate: occurrenceDayKey,
            scheduledTime: currentActionData?.scheduledTime,
            timeZone: currentActionData?.timezone,
            gmtOffset: currentActionData?.gmtOffset,
            existingExceptions: currentExceptions,
            fallbackTimeZone: timeZone,
          });

          await updateEvent.mutateAsync({
            id: currentActionData.event.id,
            recurrence: {
              rules: [
                {
                  ...(recurrenceRule as RecurrenceRule),
                  until: untilUtc,
                },
              ],
              exceptions: {
                add: splitExceptions,
              },
            },
            start: currentActionData.event.start,
            end: exceptionUtc,
          });

          invalidateProjectPlannerIfNeeded();

          toast({
            title: t('toasts.deletedThisAndAllFollowingActions'),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });

          onClose();
          handleCloseDeleteRecurringEvent();
          break;
        }

        case EventOptions.ALL_EVENTS: {
          await deleteAction.mutateAsync({ actionId: currentActionData.id });
          invalidateProjectPlannerIfNeeded();
          handleCloseDeleteRecurringEvent();
          break;
        }

        default:
          console.warn('No delete option selected');
      }
    },
    [
      currentActionData,
      updateEvent,
      deleteAction,
      toast,
      t,
      onClose,
      handleCloseDeleteRecurringEvent,
      scheduledDateUpdated,
      scheduledDateUpdatedFromStoredAction,
      timeZone,
      invalidateProjectPlannerIfNeeded,
    ],
  );

  const handleCloseEditRecurringAction = useCallback(() => {
    setIsEditRecurringEvent.off();
    setIsAllowThisRecurringEvent.off();
    setIsAllowAllRecurringEvent.off();
  }, [setIsEditRecurringEvent, setIsAllowThisRecurringEvent, setIsAllowAllRecurringEvent]);

  const handleScheduledTime = useCallback(
    (date: Date | null) => {
      if (!date) {
        setScheduledTime(null);
        // Clear recurrence when time is manually removed - only if editing
        if (isEditingAction && selectedRecurrence) {
          setSelectedRecurrence('');
          setCustomRecurrence(undefined);
        }
        return;
      }

      if (scheduledDate) {
        scheduledDate.setHours(date.getHours());
        scheduledDate.setMinutes(date.getMinutes());
      }

      setScheduledTime(scheduledDate);
    },
    [scheduledDate, isEditingAction, selectedRecurrence],
  );

  const onChangeTabs = useCallback(
    (index: string | number) => {
      if ((index as DayWeek) === WEEK) {
        setScheduledTime(null);
        // Clear recurrence when switching to WEEK tab (no time) - only if editing
        if (isEditingAction && selectedRecurrence) {
          setSelectedRecurrence('');
          setCustomRecurrence(undefined);
        }
      }

      if ((index as DayWeek) === DAY && currentActionData?.scheduledDate) {
        const localDate =
          currentActionData?.scheduledTime && currentActionData?.timezone
            ? utcToLocalDate(
                currentActionData.scheduledDate as string,
                currentActionData.scheduledTime,
                currentActionData.timezone,
                currentActionData.gmtOffset,
              )
            : formatStringToDate(currentActionData.scheduledDate as string);
        setScheduledDate(localDate);
      }

      setTabIndex(index as DayWeek);
      setDateRange((index as DayWeek) === WEEK);
    },
    [currentActionData?.scheduledDate, isEditingAction, selectedRecurrence],
  );

  const handleUpdatePromises = useCallback(async () => {
    if (currentActionData) {
      const originalPromises = currentActionData.promises;
      const isToCreate = !originalPromises?.length && relation !== ActionRelation.None && !!selectedPeople.length;
      const isToDelete = !!originalPromises?.length && !selectedPeople.length;
      const isToUpdateOne =
        originalPromises?.length === 1 &&
        selectedPeople.length === 1 &&
        (originalPromises[0].kind !== relation || originalPromises[0].personId !== selectedPeople[0]);
      const isNewLeverageRequest =
        originalPromises?.length && !originalPromises[0].leverageRequestStatus && createLeverageRequestWatcher;

      const originalIds = originalPromises?.map((promise) => promise.personId) || [];
      const arrayLeftDiff = difference(originalIds, selectedPeople);
      const arrayRightDiff = difference(selectedPeople, originalIds);

      if (isToCreate) {
        const objects = selectedPeople.map((person) => ({
          id: uuidv4(),
          actionId: currentActionData.id,
          personId: person,
          kind: relation,
        }));

        const promises = await createPromises.mutateAsync({ objects });

        if (relation === ActionRelation.Commit) {
          trackCommitmentMade(promises.insertPromise.returning[0].id);
        }

        setLeverageRequestPromise(promises.insertPromise.returning[0]);
        return promises;
      }

      if (isToDelete) {
        return deletePromises.mutateAsync({ actionId: currentActionData.id });
      }

      if (isToUpdateOne) {
        const promises = await updatePromise.mutateAsync({
          personId: selectedPeople[0],
          kind: relation,
          id: originalPromises[0].id,
        });

        if (relation === ActionRelation.Commit) {
          trackCommitmentMade(promises.updatePromiseByPk.id);
        }

        // remove old leverage request from promise if the person or relation has changed
        if (
          originalPromises[0].leverageRequestStatus &&
          (willBeNewPersonLeveraged || relation !== ActionRelation.Leverage)
        ) {
          await updatePromiseRemoveLeverageRequest.mutateAsync({
            id: originalPromises[0].id,
          });
        }
        setLeverageRequestPromise(promises.updatePromiseByPk);
        return promises;
      }

      if (arrayLeftDiff.length || arrayRightDiff.length) {
        const objects = selectedPeople.map((person) => ({
          id: uuidv4(),
          actionId: currentActionData.id,
          personId: person,
          kind: relation,
        }));
        return replacePromises.mutateAsync({
          actionId: currentActionData.id,
          objects,
        });
      }

      // if the user has decided to send a leverage request after having already chosen a person
      // and saving the action, but is now editing the action
      if (isNewLeverageRequest && !leverageRequestPromise) {
        setLeverageRequestPromise(originalPromises[0]);
      }
    }
  }, [
    createPromises,
    currentActionData,
    deletePromises,
    relation,
    replacePromises,
    selectedPeople,
    updatePromise,
    createLeverageRequestWatcher,
    updatePromiseRemoveLeverageRequest,
    willBeNewPersonLeveraged,
    leverageRequestPromise,
  ]);

  const hasPromiseAssignmentsChanged = useCallback(
    (): boolean =>
      currentActionData != null &&
      havePromiseAssignmentsChanged(currentActionData.promises, relation, selectedPeople),
    [currentActionData, relation, selectedPeople],
  );

  const onSubmitEditAction = useCallback(async () => {
    if (!currentActionData?.id) {
      return;
    }

    if (!isFormValuesChanged() && !isStarOrStatusValuesChanged() && !hasPromiseAssignmentsChanged()) {
      toast({
        title: t('toasts.actionUpdated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      return;
    }

    if (isStarred) {
      trackActionStarred();
    }

    if (validateRecurrence(currentActionData?.event?.recurrence) && selectedRecurrence) {
      if (isOpenFromRow) {
        if (isFormValuesChanged()) {
          const confirmedEditSeries = await showDialogModal();
          if (!confirmedEditSeries) {
            return;
          }
          await editRecurringEvents(EventOptions.ALL_EVENTS);
          return;
        }

        if (hasPromiseAssignmentsChanged() && !isFormValuesChanged()) {
          const confirmedSeries = await showDialogModal();
          if (!confirmedSeries) {
            return;
          }

          await handleUpdatePromises();

          if (isStarOrStatusValuesChanged()) {
            const recurrence = structuredClone(currentActionData?.event?.recurrence);
            const isDateOfStarringChanged = currentActionData?.dateOfStarring !== isStarred;
            const isProgressStatusChanged = currentActionData?.progressStatus !== completionStatus;
            if (isDateOfStarringChanged && isProgressStatusChanged) {
              if (recurrence?.exceptions?.custom) {
                recurrence.exceptions.custom = {};
              }
            } else if (isDateOfStarringChanged) {
              if (recurrence?.exceptions?.custom) {
                Object.values(recurrence.exceptions.custom).forEach((exception) => {
                  delete exception.dateOfStarring;
                });
              }
            } else if (isProgressStatusChanged) {
              if (recurrence?.exceptions?.custom) {
                Object.values(recurrence.exceptions.custom).forEach((exception) => {
                  delete exception.progressStatus;
                });
              }
            }

            updateEvent.mutate({
              ...currentActionData?.event,
              recurrence,
              start: currentActionData?.event?.start,
              id: currentActionData?.event?.id || '',
              allowCompletionRemoval: isProgressStatusChanged,
            });
          }

          pendingRecurrenceRef.current = { customRecurrence, selectedRecurrence };
          onUpdateActionOne.mutate();
          return;
        }

        const confirmedOption = await showDialogModal();
        if (!confirmedOption) {
          return;
        }
        if (!isFormValuesChanged() && isStarOrStatusValuesChanged()) {
          const recurrence = structuredClone(currentActionData?.event?.recurrence);
          const isDateOfStarringChanged = currentActionData?.dateOfStarring !== isStarred;
          const isProgressStatusChanged = currentActionData?.progressStatus !== completionStatus;
          if (isDateOfStarringChanged && isProgressStatusChanged) {
            if (recurrence?.exceptions?.custom) {
              recurrence.exceptions.custom = {};
            }
          } else if (isDateOfStarringChanged) {
            if (recurrence?.exceptions?.custom) {
              Object.values(recurrence.exceptions.custom).forEach((exception) => {
                delete exception.dateOfStarring;
              });
            }
          } else if (isProgressStatusChanged) {
            if (recurrence?.exceptions?.custom) {
              Object.values(recurrence.exceptions.custom).forEach((exception) => {
                delete exception.progressStatus;
              });
            }
          }

          updateEvent.mutate({
            ...currentActionData?.event,
            recurrence,
            start: currentActionData?.event?.start,
            id: currentActionData?.event?.id || '',
            allowCompletionRemoval: isProgressStatusChanged,
          });
          return;
        }
      } else if (!isFormValuesChanged() && isStarOrStatusValuesChanged()) {
        if (hasPromiseAssignmentsChanged()) {
          await handleUpdatePromises();
        }
        let customValues: Record<string, any> = {};
        const recurrence = structuredClone(currentActionData?.event?.recurrence);
        const key = currentActionData?.scheduledDate || '';

        recurrence!.exceptions ??= {};
        recurrence!.exceptions!.custom ??= {};
        recurrence!.exceptions!.custom[key] ??= {};

        const { dateOfStarring, progressStatus } = recurrence!.exceptions!.custom[key] ?? {};

        const isDateOfStarringChanged = currentActionData?.dateOfStarring !== isStarred;
        const isProgressStatusChanged = currentActionData?.progressStatus !== completionStatus;

        if (isDateOfStarringChanged && isProgressStatusChanged) {
          customValues = {
            progressStatus: completionStatus,
            dateOfStarring: dateOfStarring ? null : key,
          };
        } else if (isDateOfStarringChanged) {
          customValues = dateOfStarring ? { progressStatus } : { dateOfStarring: key, progressStatus };
        } else if (isProgressStatusChanged) {
          customValues = { dateOfStarring, progressStatus: completionStatus };
        }
        recurrence!.exceptions!.custom[key] = customValues;

        updateEvent.mutate({
          id: currentActionData?.event?.id as string,
          recurrence: recurrence,
          start: currentActionData?.event?.start,
        });
        onClose();
        return;
      } else if (
        hasPromiseAssignmentsChanged() &&
        !isFormValuesChanged() &&
        !isStarOrStatusValuesChanged()
      ) {
        await handleUpdatePromises();
        pendingRecurrenceRef.current = { customRecurrence, selectedRecurrence };
        onUpdateActionOne.mutate();
        return;
      } else {
        const currentFrequency = getValueInRecurrenceRule(currentActionData?.event?.recurrence, 'frequency');
        const formattedSelectedFrequency = getFrequency(
          selectedRecurrence?.toLowerCase(),
          customRecurrence?.selectedRecurrence,
        );
        if (currentFrequency === formattedSelectedFrequency) setIsAllowThisRecurringEvent.on();
        let comparisonDate = scheduledDate && format(scheduledDate, 'yyyy-MM-dd');
        if (scheduledDate && scheduledTime) {
          comparisonDate = localToGivenTimezoneDateToUTC(scheduledDate, timeZone).scheduledDate;
        }

        if (comparisonDate === scheduledDateUpdated) setIsAllowAllRecurringEvent.on();
        setIsEditRecurringEvent.on();
        return;
      }
    }

    if (!currentActionData?.event?.id && scheduledDate) {
      await createEvent.mutateAsync({
        id: uuidv4(),
        actionId: currentActionData?.id,
      });
    }
    await handleUpdatePromises();

    // Resolve weekly plan for the current date so existing actions with wrong week get fixed on re-save
    if (scheduledDate && tabIndex === DAY) {
      resolvedWeeklyPlanIdRef.current =
        (await getOrCreateWeeklyPlanId(scheduledDate)) ?? getWeeklyPlanId(scheduledDate) ?? undefined;
    }

    pendingRecurrenceRef.current = { customRecurrence, selectedRecurrence };
    onUpdateActionOne.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createEvent,
    currentActionData?.event?.id,
    currentActionData?.id,
    customRecurrence,
    handleUpdatePromises,
    hasPromiseAssignmentsChanged,
    onUpdateActionOne,
    scheduledDate,
    selectedRecurrence,
    isStarred,
  ]);

  const onStarringAction = () => {
    setIsStarred((prevState) => {
      return prevState ? null : new Date(Date.now());
    });
  };

  const onClickProgressStatus = (progressStatus: ProgressStatus) => {
    setCompletionStatus(progressStatus);
    currentActionData && trackActionProgress(progressStatus, currentActionData);

    if (progressStatus === 'complete') {
      playCompleteAudio();
    }
  };

  const onCloseModal = useCallback(() => {
    onClose();
    setSelectedProject(null);
    onDatePickerClose();
    getCurrentActionData(null);
    initialized.current = false;
  }, [onClose, onDatePickerClose, getCurrentActionData]);

  const onSubmitCreateAction = useCallback(
    async (form: ActionFormData, allowRecurrence: boolean = true) => {
      const inBlockForRecurrence = Boolean(blockId || currentActionData?.blockId || disallowActionRecurrence);
      const recurrenceAllowedForSubmit = allowRecurrence && !inBlockForRecurrence;

      // Resolve weekly plan for the date picker at submit time so we never send a stale
      // weeklyPlanId (e.g. from a different week). Critical for timezones: Sunday 6 PM MST
      // must belong to the week containing that Sunday, not the week of Monday UTC.
      let planAnchorLocal: Date | null = null;
      if (scheduledDate) {
        planAnchorLocal = new Date(scheduledDate);
        if (scheduledTime) {
          planAnchorLocal.setHours(
            scheduledTime.getHours(),
            scheduledTime.getMinutes(),
            scheduledTime.getSeconds(),
            0,
          );
        }
      }

      let byDayForCreateAlign: { day: string }[] = [];
      if (selectedRecurrence && recurrenceAllowedForSubmit && planAnchorLocal) {
        const recurrenceType = selectedRecurrence.toLowerCase();
        const freq = normalizeRecurrenceRuleFrequency(
          getFrequency(recurrenceType, customRecurrence?.selectedRecurrence) || '',
        );
        if (freq === 'weekly') {
          const isWeekdayOnly = isEveryWeekdayLabel(recurrenceType);
          const currentRecurrenceRules = currentActionData?.event?.recurrence?.rules?.[0];
          const byDayFromExisting = currentRecurrenceRules?.by_day || [];
          if (isWeekdayOnly) {
            byDayForCreateAlign = weekdayOnly;
          } else if (customRecurrence?.selectedWeekDays?.length) {
            byDayForCreateAlign = customRecurrence.selectedWeekDays;
          } else if (byDayFromExisting.length) {
            byDayForCreateAlign = byDayFromExisting;
          }
          if (byDayForCreateAlign.length > 0 && weeklyByDayAnchorNeedsAlignment(planAnchorLocal, byDayForCreateAlign)) {
            planAnchorLocal = alignLocalDatetimeToFirstWeeklyByDay(planAnchorLocal, byDayForCreateAlign);
          }
        }
      }

      let currentWeeklyPlan: string | undefined = undefined;
      if (planAnchorLocal) {
        currentWeeklyPlan = (await getOrCreateWeeklyPlanId(planAnchorLocal)) ?? undefined;
        if (!currentWeeklyPlan) {
          currentWeeklyPlan = getWeeklyPlanId(planAnchorLocal) ?? undefined;
        }
      }

      const combinedForCreate =
        planAnchorLocal && scheduledTime
          ? localToGivenTimezoneDateToUTC(planAnchorLocal, timeZone)
          : null;
      const newScheduledTZDate =
        combinedForCreate?.scheduledDate ??
        (planAnchorLocal && formatDateToString(planAnchorLocal)) ??
        (scheduledDate && formatDateToString(scheduledDate));

      createAction.mutate(
        {
          blockId,
          categoryId: selectedCategory?.id ?? '',
          dateOfStarring: isStarred,
          duration: form.time,
          id: uuidv4(),
          isLocked: false,
          notes: form.notes ?? '',
          progressStatus: completionStatus ?? INCOMPLETE,
          projectId: selectedProject?.id ?? null,
          scheduledDate: tabIndex === DAY ? newScheduledTZDate : null,
          scheduledTime:
            combinedForCreate?.scheduledTime ??
            (scheduledTime ? localToGivenTimezoneDateToUTC(scheduledTime, timeZone).scheduledTime : null),
          timezone: timeZone,
          title: form.name,
          weeklyPlanId: scheduledDate ? currentWeeklyPlan : undefined,
          gmtOffset: getGMTOffset(timeZone),
          isCanceled: false,
        },
        {
          onSuccess: async (data) => {
            const { id: actionId } = data.insertActionOne;

            if (data.insertActionOne.scheduledDate) {
              const currentExceptions = currentActionData?.event?.recurrence?.exceptions || {};
              const recurrenceType = selectedRecurrence?.toLowerCase();

              const currentRecurrenceRules = currentActionData?.event?.recurrence?.rules?.[0];

              const interval = currentRecurrenceRules?.interval;
              const count = currentRecurrenceRules?.count;
              const byDay = currentRecurrenceRules?.by_day || [];

              const isWeekdayOnly = isEveryWeekdayLabel(recurrenceType);
              const frequency = getFrequency(recurrenceType, customRecurrence?.selectedRecurrence);

              let by_day: { day: string }[] = [];

              if (isWeekdayOnly) {
                by_day = weekdayOnly;
              } else if (customRecurrence && customRecurrence?.selectedWeekDays?.length > 0) {
                by_day = customRecurrence.selectedWeekDays;
              } else if (byDay?.length > 0) {
                by_day = byDay;
              }

              const carriedUntil = pendingOriginalUntilForFollowingRef.current;
              pendingOriginalUntilForFollowingRef.current = undefined;

              const newStartKey = (newScheduledTZDate ?? '').split('T')[0];
              let untilForNewRule: string | undefined;
              if (customRecurrence?.scheduledDate != null) {
                untilForNewRule = formatDateToString(customRecurrence.scheduledDate);
              } else if (carriedUntil != null && carriedUntil !== '' && newStartKey && carriedUntil >= newStartKey) {
                untilForNewRule = carriedUntil;
              }

              const newRecurrenceRule = {
                frequency: normalizeRecurrenceRuleFrequency(frequency || ''),
                interval: customRecurrence?.interval || interval || 1,
                ...(customRecurrence?.occurrences ? { count: customRecurrence.occurrences } : count ? { count } : {}),
                ...(by_day.length > 0 ? { by_day } : {}),
                ...(untilForNewRule ? { until: untilForNewRule } : {}),
              };

              const recurrenceForApi = {
                rules: [newRecurrenceRule],
                exceptions: currentExceptions,
              };

              const recurrenceData =
                selectedRecurrence && recurrenceAllowedForSubmit && validateRecurrence(recurrenceForApi)
                  ? {
                      recurrence: recurrenceForApi,
                      start: newScheduledTZDate,
                      end:
                        customRecurrence?.scheduledDate != null
                          ? formatDateToString(customRecurrence.scheduledDate)
                          : untilForNewRule ?? null,
                    }
                  : {};

              const eventData = {
                id: uuidv4(),
                actionId,
                ...recurrenceData,
              };

              const event = await createEvent.mutateAsync(eventData);

              if (event?.insertEventOne) {
                data.insertActionOne.event = {
                  id: event.insertEventOne.id,
                  typeName: 'Event',
                };
              }
            } else {
              pendingOriginalUntilForFollowingRef.current = undefined;
            }

            if (relation !== ActionRelation.None && selectedPeople.length) {
              const objects = selectedPeople.map((person) => ({
                id: uuidv4(),
                actionId,
                personId: person,
                kind: relation,
              }));

              const { insertPromise } = await createPromises.mutateAsync({ objects });
              // we are only supporting one "promise" creation (leverage request) at a time right now
              setLeverageRequestPromise(insertPromise.returning[0]);

              data.insertActionOne.promises = insertPromise.returning;

              if (insertPromise.returning[0].kind === ActionRelation.Commit) {
                trackCommitmentMade(insertPromise.returning[0].id);
              }
            }

            if (isStarred) {
              trackActionStarred();
            }

            refetchActions(data.insertActionOne);

            toast({
              title: t('toasts.actionCreatedSuccessfully'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });

            if (createLeverageRequestWatcher) {
              onClose();
              setCreateLeverageModalOpen(true);
            } else {
              onCloseModal();
            }
          },
          onError: () => {
            pendingOriginalUntilForFollowingRef.current = undefined;
          },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onClose,
      scheduledDate,
      scheduledTime,
      createAction,
      blockId,
      selectedCategory?.id,
      isStarred,
      selectedProject?.id,
      tabIndex,
      weeklyPlanId,
      relation,
      selectedPeople,
      onCreateAction,
      toast,
      onCloseModal,
      createEvent,
      createPromises,
      createLeverageRequestWatcher,
      setCreateLeverageModalOpen,
      timeZone,
      t,
    ],
  );

  const openEmailModalOrSubmit = useCallback(
    (data: ActionFormData) => {
      if (createLeverageRequestWatcher && allowCreateLeverageRequest && !selectedPerson?.email) {
        onAddEmailModalOpen();
      } else if (isEditingAction) {
        setFormData(data);
        onSubmitEditAction();
      } else {
        onSubmitCreateAction(data);
        eventLocation && trackActionCreated(eventLocation);
      }
    },
    [
      onAddEmailModalOpen,
      onSubmitEditAction,
      onSubmitCreateAction,
      isEditingAction,
      createLeverageRequestWatcher,
      selectedPerson?.email,
      allowCreateLeverageRequest,
      eventLocation,
    ],
  );

  const onClickUnplan = useCallback(() => {
    setScheduledDate(null);
    setScheduledTime(null);
    // Clear recurrence when unplanning - only if editing
    if (isEditingAction && selectedRecurrence) {
      setSelectedRecurrence('');
      setCustomRecurrence(undefined);
    }
    onDatePickerClose();
  }, [onDatePickerClose, isEditingAction, selectedRecurrence]);

  useEffect(() => {
    if (!isDatePickerOpen && (hasDateChanged || hasChangedTabs)) {
      getWeeklyPlan();
    }
  }, [getWeeklyPlan, hasDateChanged, hasChangedTabs, isDatePickerOpen]);

  // @TODO: Improve accessibility for modal and datepicker
  // Manually control close on esc for date picker
  useEffect(() => {
    const closeOnEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseModal();
      }
    };
    if (isDatePickerOpen) {
      window.addEventListener('keydown', closeOnEsc, true);
    }
    return () => window.removeEventListener('keydown', closeOnEsc, true);
  }, [getCurrentActionData, isDatePickerOpen, onDatePickerClose, onCloseModal]);

  useEffect(() => {
    if (isEditingAction) {
      if (currentActionData?.title) setValue('name', currentActionData?.title);
      if (currentActionData?.duration)
        setValue('time', currentActionData?.duration?.substring(0, currentActionData?.duration.length - 3));
      if (currentActionData?.dateOfStarring) setIsStarred(currentActionData?.dateOfStarring);
      if (currentActionData?.notes) setValue('notes', currentActionData?.notes);

      if (currentActionData?.scheduledDate && currentActionData?.scheduledTime) {
        const scheduledDateWithTime = utcToLocalDate(
          currentActionData.scheduledDate,
          currentActionData.scheduledTime,
          currentActionData.timezone,
          currentActionData.gmtOffset,
        );
        setTabIndex(DAY);
        setDateRange(false);
        setScheduledTime(scheduledDateWithTime);
        setScheduledDate(scheduledDateWithTime);
      }

      if (currentActionData?.promises) {
        setRelation(currentActionData?.promises[0]?.kind ?? ActionRelation.None);
        setSelectedPeople(() => currentActionData?.promises?.map((promise) => promise.personId) || []);
      }
      setCompletionStatus(currentActionData?.progressStatus ?? INCOMPLETE);
    }
    return () => {
      reset();
      setIsStarred(null);
    };
     
  }, [
    reset,
    setValue,
    isEditingAction,
    currentActionData?.title,
    currentActionData?.duration,
    currentActionData?.dateOfStarring,
    currentActionData?.notes,
    currentActionData?.scheduledTime,
    currentActionData?.scheduledDate,
    currentActionData?.promises,
    currentActionData?.timezone,
    currentActionData?.gmtOffset,
    currentActionData?.progressStatus,
  ]);

  useEffect(() => {
    if (isOpen) {
      onSetCategory();
      onSetProject();
    }
  }, [isOpen, onSetCategory, onSetProject]);

  useEffect(() => {
    if (scheduledDate || !isDatePickerOpen) {
      return;
    }

    if (isDatePickerOpen) {
      setScheduledDate(new Date());
      return;
    }
  }, [isDatePickerOpen, scheduledDate]);

  useEffect(() => {
    if (relation !== ActionRelation.Leverage) {
      setValue('createLeverageRequest', false);
    }
  }, [relation, setValue]);

  const changingLeverage = useMemo(
    () =>
      leverageStatus &&
      (!isEqual([currentlyLeveragedPersonId], selectedPeople) || relation !== ActionRelation.Leverage),
    [leverageStatus, currentlyLeveragedPersonId, selectedPeople, relation],
  );

  useEffect(() => {
    if (
      leverageStatus &&
      !willBeNewPersonLeveraged &&
      selectedPeople?.length > 0 &&
      relation === ActionRelation.Leverage
    ) {
      setValue('createLeverageRequest', true);
    } else if (changingLeverage) {
      setValue('createLeverageRequest', false);
    }
  }, [willBeNewPersonLeveraged, leverageStatus, setValue, selectedPeople, relation, changingLeverage]);

  useEffect(() => {
    if (isOpen) {
      // Does not work without setTimeout
      // must be removed after focus trap is enabled
      // (lookup the comments in the codebase by "RRI-2918")
      const timeoutId = setTimeout(() => setFocus('name'), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, setFocus]);

  return (
    <>
      <StyledModal isOpen={isOpen} onClose={onCloseModal}>
        <form onSubmit={handleSubmit(openEmailModalOrSubmit)}>
          <ModalBody>
            <HStack alignItems="flex-start" paddingTop={rem(24)}>
              <Box flex="1" overflow="auto">
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { ref, value, onChange } }) => (
                    <Input
                      ref={ref}
                      onChange={onChange}
                      value={value}
                      error={errors.name?.message}
                      color="text-primary"
                      fontWeight="medium"
                      fontSize={rem(20)}
                      autoComplete="off"
                      _hover={{
                        boxShadow: 'none',
                      }}
                      _focusVisible={{
                        boxShadow: 'none',
                        borderColor: 'cyan.400 !important',
                      }}
                      borderBottomWidth={rem(1)}
                      borderBottomStyle="solid"
                      borderBottomColor="stroke-primary"
                      placeholder={t('labels.title')}
                      _placeholder={{ color: 'gray.400', fontSize: rem(24) }}
                    />
                  )}
                />
              </Box>
              {isEditingAction &&
                (isSeriesLevelRecurringStatusReadOnly ? (
                  <Tooltip
                    label={t('tooltips.recurringSeriesProgressDisabled')}
                    placement="bottom"
                    shouldWrapChildren
                  >
                    {/* eslint-disable-next-line chakra-ui/props-order -- span wrapper for Tooltip + disabled control */}
                    <Box as="span" bottom={rem(-6)} display="inline-block" lineHeight={0} position="relative">
                      <IconProgressState
                        dimension={rem(40)}
                        state={currentActionData?.progressStatus ?? 'incomplete'}
                        onClick={onClickProgressStatus}
                        position="relative"
                        bottom={0}
                        disabled
                      />
                    </Box>
                  </Tooltip>
                ) : (
                  <IconProgressState
                    dimension={rem(40)}
                    state={currentActionData?.progressStatus ?? 'incomplete'}
                    onClick={onClickProgressStatus}
                    position="relative"
                    bottom={rem(-6)}
                  />
                ))}
            </HStack>

            <FormControl isInvalid={!!errors.notes?.message}>
              <Controller
                name="notes"
                control={control}
                render={({ field: { ref, value, onChange } }) => (
                  <Textarea
                    ref={ref}
                    minHeight={rem(70)}
                    marginTop={rem(30)}
                    padding={rem(16)}
                    border={`${rem(1)} solid`}
                    borderColor="stroke-primary"
                    borderRadius={rem(4)}
                    _hover={{
                      boxShadow: 'none',
                    }}
                    _focusVisible={{
                      borderColor: 'cyan.400',
                    }}
                    _placeholder={{ color: 'gray.400', fontSize: rem(16) }}
                    resize="none"
                    onChange={onChange}
                    placeholder={t('labels.addNotes')}
                    value={value}
                  />
                )}
              />
            </FormControl>

            <Flex justifyContent="space-between" width="full" marginTop={rem(16)}>
              <HStack position="relative" flexWrap="wrap" width="full">
                <CategoriesMenu
                  category={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                  shouldCreateCategory={false}
                  readonly={!isEmpty(currentActionData?.project) || !!currentActionData?.blockId || readonlyCategory}
                  readonlyTooltipLabel={readOnlyTooltip}
                />

                <VStack align="start">
                  <Controller
                    name="time"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <InputDuration value={value} onChange={onChange} variant="bordered" />
                    )}
                  />
                  {errors.time && (
                    <Box as="span" position="absolute" bottom={rem(-25)} color="red.500" fontSize="sm">
                      {errors.time.message}
                    </Box>
                  )}
                </VStack>

                <IconStarred
                  starred={Boolean(isStarred)}
                  onClick={onStarringAction}
                  _hover={{ backgroundColor: 'background-quaternary' }}
                  minWidth={rem(28)}
                  minHeight={rem(28)}
                  height={rem(28)}
                  width={rem(28)}
                  border={`${rem(1)} solid var(--chakra-colors-stroke-primary)`}
                />

                <RPMDateTimePickerWithPlaceholder
                  isDatePickerOpen={isDatePickerOpen}
                  scheduledTime={scheduledTime}
                  tabIndex={tabIndex}
                  onDatePickerOpen={onDatePickerOpen}
                  onClickUnplan={onClickUnplan}
                  dateRange={dateRange}
                  onChangeTabs={onChangeTabs}
                  onSave={onHandlePlansIds}
                  onCancel={onDatePickerClose}
                  scheduledDate={scheduledDate}
                  updateScheduledTime={handleScheduledTime}
                  updateSelectedDate={setScheduledDate}
                  withTimePicker={tabIndex === DAY}
                  selectedTimezone={selectedTimezone}
                  setSelectedTimezone={setSelectedTimezone}
                  variant="input"
                  border={`${rem(1)} solid var(--chakra-colors-stroke-primary)`}
                />
              </HStack>
            </Flex>

            <Divider marginTop={errors.time ? rem(32) : rem(16)} opacity="1" borderColor="whiteAlpha.300" />
            {/* //remove this condition after delete the flags (SHOW_RECURRENT_EVENTS_FOR_ALL_USERS; SHOW_RECURRENT_EVENTS_FOR_BETA_USERS) */}
            {shouldShowRecurrenceFeature && (
              <>
                <HStack alignItems="flex-start" gap={rem(16)} marginTop={rem(11)}>                
                  <Text textStyle="small" paddingTop={rem(4)}>
                    {t('labels.recurrence')}
                  </Text>
                  <Tooltip
                    hasArrow
                    isDisabled={!recurringActionIsDisabledInBlock}
                    label={recurringActionIsDisabledInBlock ? t('tooltips.recurringActionIsDisabledInBlock') : undefined}
                    placement="top-start"
                    shouldWrapChildren
                  >
                    <RecurrenceMenu
                      scheduleDate={
                        scheduledDate
                          ? scheduledDate
                          : currentActionData?.scheduledDate
                            ? utcToLocalDate(
                                currentActionData?.scheduledDate,
                                currentActionData?.scheduledTime,
                                currentActionData?.timezone,
                                currentActionData?.gmtOffset,
                              )
                            : undefined
                      }
                      variant="bordered"
                      categoryId={selectedCategory?.id}
                      recurrence={displayRecurrenceLabel ?? selectedRecurrence}
                      interval={customRecurrence?.interval ?? +interval}
                      byDays={customRecurrence?.selectedWeekDays ?? byDays}
                      setSelectedRecurrence={setSelectedRecurrence}
                      readonly={!scheduledTime || recurringActionIsDisabledInBlock}
                      setCustomRecurrence={setCustomRecurrence}
                      occurrences={
                        customRecurrence != null
                          ? customRecurrence.occurrences ?? undefined
                          : recurrenceCountFromRule != null
                            ? Number(recurrenceCountFromRule)
                            : undefined
                      }
                      endDate={customRecurrence?.scheduledDate ?? savedEndDateFromAction}
                      recurrenceFrequency={
                        customRecurrence
                          ? (customRecurrence.selectedRecurrence?.toLowerCase() === 'day'
                              ? 'daily'
                              : customRecurrence.selectedRecurrence?.toLowerCase() === 'week'
                                ? 'weekly'
                                : customRecurrence.selectedRecurrence?.toLowerCase() === 'month'
                                  ? 'monthly'
                                  : customRecurrence.selectedRecurrence?.toLowerCase() === 'year'
                                    ? 'yearly'
                                    : undefined)
                          : (frequency as 'daily' | 'weekly' | 'monthly' | 'yearly' | undefined)
                      }
                      />
                  </Tooltip>
                </HStack>

                <Divider marginTop={errors.time ? rem(32) : rem(11)} opacity="1" borderColor="whiteAlpha.300" />
              </>
            )}

            {showProjectSelector && (
              <>
                <HStack gap={rem(16)} marginTop={rem(11)}>
                  <Text textStyle="small">{t('labels.project')}</Text>
                  <ProjectsMenu
                    variant="bordered"
                    categoryId={selectedCategory?.id}
                    project={selectedProject}
                    setSelectedProject={setSelectedProject}
                    readonly={readonlyProject}
                  />
                </HStack>

                {currentActionData?.projectId !== (selectedProject?.id ?? null) &&
                  currentActionData?.isLastBlockAction &&
                  visibleProjectSection && (
                    <HStack alignItems="center" marginTop={rem(11)}>
                      <Icon as={IconWarning} boxSize={rem(12)} color="amber.400" />
                      <Text color="amber.400" fontSize="xs" fontWeight="normal">
                        {t('labels.changingProjectWillRemoveAction')}
                      </Text>
                    </HStack>
                  )}

                <Divider marginTop={rem(11)} opacity="1" borderColor="whiteAlpha.300" />
              </>
            )}

            {currentActionData?.block?.result && (
              <VStack alignItems="flex-start" gap="0">
                <HStack gap={rem(16)} paddingY={rem(12)}>
                  <Text textStyle="small">{t('labels.block')}</Text>
                  <Text fontSize="sm">{currentActionData.block.result}</Text>
                </HStack>
                <Divider opacity="1" borderColor="whiteAlpha.300" />
              </VStack>
            )}

            <Flex justifyContent="space-between" width="full" marginTop={rem(11)}>
              <HStack gap={rem(16)}>
                <Text textStyle="small">{t('labels.leverageCommit')}</Text>
                <ManageActionRelations
                  initialRelation={currentActionData?.promises?.[0]?.kind ?? relation}
                  onRelationChange={setRelation}
                  onSelectPeople={setSelectedPeople}
                  selectedPeople={selectedPeople}
                  leverageStatus={
                    !willBeNewPersonLeveraged && relation === ActionRelation.Leverage ? leverageStatus : undefined
                  }
                  onPersonAvatarClick={
                    !willBeNewPersonLeveraged && relation === ActionRelation.Leverage && leverageStatus
                      ? openLeverageRequestModal
                      : undefined
                  }
                  variant="bordered"
                />
              </HStack>

              <Controller
                name="createLeverageRequest"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    sx={{
                      '.chakra-checkbox__control': {
                        background: isDarkMode ? 'gray.100' : 'gray.200 !important',
                      },
                    }}
                    isChecked={value}
                    isDisabled={
                      !(relation === ActionRelation.Leverage && selectedPeople.length) || !allowCreateLeverageRequest
                    }
                    onChange={onChange}
                    size="sm"
                    variant="round"
                  >
                    <Text as="span" textStyle="large">
                      {t('labels.createLeverageRequest')}
                    </Text>
                  </Checkbox>
                )}
              />
            </Flex>

            {changingLeverage && (
              <HStack alignItems="center" marginTop={rem(11)}>
                <Icon as={IconWarning} boxSize={rem(12)} color="amber.400" />
                <Text color="amber.400" fontSize="xs" fontWeight="normal">
                  {t('labels.changingPersonWillCancelRequests')}
                </Text>
              </HStack>
            )}

            <Divider marginTop={rem(11)} opacity="1" borderColor="whiteAlpha.300" />
          </ModalBody>
          <ModalFooter justifyContent={isEditingAction ? 'space-between' : 'flex-end'} gap={rem(16)} display="flex">
            {isEditingAction && (
              <IconButton
                aria-label={t('buttons.deleteAction')}
                icon={<Icon as={IconTrash} boxSize={rem(20)} />}
                onClick={() => {
                  // Persisted recurrence must be valid; a corrupt rule (e.g. empty frequency) is treated as non-recurring.
                  const hasRecurrence =
                    validateRecurrence(currentActionData?.event?.recurrence) ||
                    (Boolean(selectedRecurrence?.length) && !isOpenFromRow && Boolean(currentActionData?.scheduledDate));

                  if ((isOpenFromRow && hasRecurrence) || !hasRecurrence) {
                    setIsDeleteModal.on();
                    return;
                  }

                  if (hasRecurrence) {
                    setIsDeleteRecurringEventModal.on();
                    return;
                  }

                  // Handle regular actions
                  setIsDeleteModal.on();
                }}
                variant="secondary"
              />
            )}
            <HStack gap={rem(16)}>
              <Button onClick={onCloseModal} size="lg" variant="secondary">
                {t('buttons.cancel')}
              </Button>
              <Button
                isDisabled={
                  onUpdateActionOne.isLoading ||
                  checkIfOnlySpaces(actionDataWatcher) ||
                  weeklyPlanQueryResult?.isFetching
                }
                size="lg"
                type="submit"
                variant="primary"
              >
                {isEditingAction ? t('buttons.updateAction') : t('buttons.saveAction')}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </StyledModal>

      {isDeleteModal && (
        <DeleteActionModal
          isOpen={isDeleteModal}
          onCloseDeleteActionModal={handleCloseDelete}
          onDeleteAction={handleDelete}
          hasFrequency={validateRecurrence(currentActionData?.event?.recurrence)}
        />
      )}

      {isDeleteRecurringEventModal && (
        <DeleteRecurringEventModal
          isOpen={isDeleteRecurringEventModal}
          onCancel={handleCloseDeleteRecurringEvent}
          onConfirm={deleteRecurringEvents}
          isSameDay={true}
          isAllowThisEventOption={true}
        />
      )}

      {isEditRecurringEvent && (
        <RecurringEventModal
          isOpen={isEditRecurringEvent}
          onCancel={handleCloseEditRecurringAction}
          onConfirm={editRecurringEvents}
          event={currentActionData}
          isSameDay={isAllowAllRecurringEvent}
          isAllowThisEventOption={isAllowThisRecurringEvent}
          mode="edit"
        />
      )}

      {isOpenRecurringConfirmationModal && (
        <RecurringConfirmationModal
          isOpen={isOpenRecurringConfirmationModal}
          onCloseConfirmationModal={onCloseRecurringConfirmationModal}
          setResolveConfirmPromise={setResolveConfirmPromise}
        />
      )}

      {selectedPerson && (
        <AddEmailToPersonModal
          isOpen={isAddEmailModalOpen}
          onClose={() => onAddEmailModalClose()}
          person={selectedPerson}
          onSuccess={() => {
            if (isEditingAction) {
              onSubmitEditAction();
            } else {
              handleSubmit(onSubmitCreateAction as any);
            }
          }}
        />
      )}

      {selectedPerson && leverageRequestPromise && (
        <CreateLeverageRequestModal
          isOpen={createLeverageModalOpen}
          onClose={() => {
            setCreateLeverageModalOpen(false);
            getCurrentActionData(null);
            reset();
          }}
          title={actionDataWatcher}
          person={selectedPerson}
          dueDate={scheduledDate}
          tabIndex={tabIndex}
          leverageRequestPromise={leverageRequestPromise}
        />
      )}

      {currentActionData?.promises?.[0] && (
        <LeverageRequestModal
          promise={currentActionData.promises[0]}
          title={currentActionData.title}
          isOpen={isOpenLeverageRequestModal}
          onClose={onCloseLeverageRequestModal}
          onSuccess={() => {
            const newPromises = currentActionData.promises?.map((promise) => ({
              ...promise,
              leverageRequestDueDate: null,
              leverageRequestRepliedAt: null,
              leverageRequestReplyText: null,
              leverageRequestStatus: null,
              leverageRequestText: null,
              leverageRequestedAt: null,
            }));
            getCurrentActionData({ ...currentActionData, promises: newPromises });
            setValue('createLeverageRequest', false);
          }}
        />
      )}
    </>
  );
};

export default ActionModal;